import { useEffect, useRef } from "react";
import { Check, Tag, X } from "lucide-react";
import useMediaQuery from "../../../../core/hooks/useMediaQuery";
import { LEGAL_LINK_MAP, LEGAL_LINK_ROUTES } from "../../../components/PublicChrome/publicSitePolicy";
import { formatBRL, periodicitySuffix } from "../lib/format";
import { PLAN_GROUPS, planGroupId } from "../lib/planGroups";
import { Plan } from "../types";
import { categoryLabel } from "./ProductCard";
import "./PlanDetailsModal.css";

const FOCUSABLE_SELECTOR =
  "a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled])";

const DETAILS_SHEET_QUERY = "(max-width: 1023px)";

/** Same geometry as the `@media (max-width: 1023px)` sheet in PlanDetailsModal.css. */
const SHEET_OVERLAY_STYLE = {
  display: "flex",
  alignItems: "stretch",
  padding: "40px 0 0",
} as const;

const SHEET_DIALOG_STYLE = {
  flex: "1 1 auto",
  width: "100%",
  maxWidth: "none",
  height: "auto",
  minHeight: 0,
  borderRadius: "24px 24px 0 0",
} as const;

const SHEET_ACTIONS_STYLE = { flexDirection: "column-reverse" } as const;

function visibleFocusable(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
    (element) => element.getClientRects().length > 0,
  );
}

function planKicker(plan: Plan): string {
  const parts = [categoryLabel(plan), PLAN_GROUPS.find((item) => item.id === planGroupId(plan))?.label].filter(
    (part): part is string => Boolean(part),
  );
  return parts.join(" \u00b7 ").toLocaleUpperCase("pt-BR");
}

function accessLine(amountOfDays: unknown): string | null {
  if (typeof amountOfDays !== "number" || !Number.isInteger(amountOfDays) || amountOfDays < 1) {
    return null;
  }
  const unit = amountOfDays === 1 ? "dia" : "dias";
  return `Acesso por ${amountOfDays} ${unit} \u00b7 não renova sozinho`;
}

interface PlanDetailsModalProps {
  plan: Plan | null;
  loading?: boolean;
  onClose: () => void;
  onSubscribe: (plan: Plan) => void;
}

export function PlanDetailsModal({ plan, loading = false, onClose, onSubscribe }: PlanDetailsModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const sheet = useMediaQuery(DETAILS_SHEET_QUERY);

  // Re-run only when the open plan changes. onClose is read from a ref.
  useEffect(() => {
    const planId = plan?.id;
    if (!planId) return;

    // preventScroll: the node sits at the end of the catalog, but the sheet is fixed to the viewport.
    dialogRef.current?.focus({ preventScroll: true });

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        if (event.ctrlKey || event.metaKey || event.altKey || event.shiftKey || event.repeat) return;
        event.preventDefault();
        onCloseRef.current();
        return;
      }

      if (event.key !== "Tab" || event.ctrlKey || event.metaKey || event.altKey) return;

      event.preventDefault();
      const dialog = dialogRef.current;
      if (!dialog) return;

      const items = visibleFocusable(dialog);
      if (items.length === 0) return;

      const active = document.activeElement;
      const index = active instanceof HTMLElement ? items.indexOf(active) : -1;

      if (event.shiftKey) {
        const next = index <= 0 ? items.length - 1 : index - 1;
        items[next].focus();
        return;
      }

      const next = index === -1 || index >= items.length - 1 ? 0 : index + 1;
      items[next].focus();
    };

    document.addEventListener("keydown", onKeyDown);

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
      document.getElementById(`ver-detalhes-${planId}`)?.focus();
    };
  }, [plan?.id]);

  if (!plan) return null;

  const kicker = planKicker(plan);
  const suffix = periodicitySuffix(plan.periodicityId);
  const line = accessLine(plan.amountOfDays);

  const overlay = (
    <div className="plan-details-overlay" style={sheet ? SHEET_OVERLAY_STYLE : undefined}>
      <div className="plan-details-scrim" onClick={onClose} />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="plan-details-title"
        data-presentation={sheet ? "sheet" : "dialog"}
        className="plan-details-dialog"
        style={sheet ? SHEET_DIALOG_STYLE : undefined}
        tabIndex={-1}
        ref={dialogRef}
      >
        <header className="plan-details-header">
          <div className="plan-details-heading">
            {kicker ? <p className="plan-details-kicker">{kicker}</p> : null}
            <h2 id="plan-details-title" className="plan-details-title">
              {plan.name}
            </h2>
          </div>
          <button
            type="button"
            className="plan-details-close"
            aria-label="Fechar"
            onClick={onClose}
            style={{ width: 44, height: 44, minWidth: 44, minHeight: 44 }}
          >
            <X aria-hidden size={24} />
          </button>
        </header>
        <div className="plan-details-body">
          {plan.description.trim() ? <p className="plan-details-description">{plan.description}</p> : null}
          {line !== null ? (
            <p className="plan-details-access">
              {line}
              {LEGAL_LINK_MAP.reembolso ? (
                <>
                  {" \u00b7 "}
                  <a
                    className="plan-details-access-link"
                    href={LEGAL_LINK_ROUTES.reembolso.to}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Regras de reembolso
                  </a>
                </>
              ) : null}
            </p>
          ) : null}
          {plan.benefits.length > 0 ? (
            <>
              <h3 className="plan-details-features-title">Recursos</h3>
              <ul className="plan-details-features">
                {plan.benefits.map((benefit, index) => (
                  <li key={`${benefit}-${index}`} className="plan-details-feature">
                    <Check aria-hidden className="plan-details-feature-icon" size={18} />
                    <span className="plan-details-feature-label">{benefit}</span>
                  </li>
                ))}
              </ul>
            </>
          ) : null}
        </div>
        <footer className="plan-details-footer">
          <p className="plan-details-price-row">
            <span className="plan-details-price">{formatBRL(plan.price)}</span>
            {suffix ? <span className="plan-details-period">{suffix}</span> : null}
          </p>
          <p className="plan-details-caption">
            <Tag aria-hidden className="plan-details-caption-icon" size={14} />
            Preço atual do site
          </p>
          <p className="plan-details-note">
            [OQ-10 / BUG-03 — oferta e preço final: decisão do CEO · não publicar este aviso]
          </p>
          <div className="plan-details-actions" style={sheet ? SHEET_ACTIONS_STYLE : undefined}>
            <button type="button" className="plan-details-back" onClick={onClose}>
              Voltar aos planos
            </button>
            <button
              type="button"
              className="plan-details-subscribe"
              disabled={loading}
              aria-busy={loading || undefined}
              onClick={() => onSubscribe(plan)}
            >
              Assinar
            </button>
          </div>
        </footer>
      </div>
    </div>
  );

  // PlansPage already renders this inside .public-pages. A portal to document.body
  // would leave that stylesheet and Montserrat (the public sheet on .public-pages); a portal to the first
  // .public-pages would also miss the signed-in catalog, which is a later copy.
  return overlay;
}
